import { Pencil } from 'assets/icons';
import Label from 'components/crm/ui/Label';
import { LABEL_CLASS } from 'components/crm/ui/fieldStyles';
import SeeMoreButton from 'components/seeMore/SeeMoreButton';
import TaskLinksField, { LINKS_PLACEHOLDER } from 'components/tasks/form/TaskLinksField';
import { linkKey, linkPayload, taskLinks } from 'helper/taskLinks';
import useText from 'hooks/useText';
import { useEffect, useRef, useState } from 'react';
import TaskContactCard from './TaskContactCard';
import useInlineField from './useInlineField';

// The linked records shown before « Voir plus ».
const SHOWN_LINKS = 3;
const sameLinks = (a, b) => a.map(linkKey).join('|') === b.map(linkKey).join('|');

/**
 * The records a task is linked to, in its panel: one frame, a line between
 * each, the first three only until « Voir plus »; each card opens its record
 * (`openerOf(link)`). A click on « Lié à » puts the form's field in place, its
 * chips the task's links: Enter, a click or a tab out of it saves them all at
 * once (several are picked in a row), Escape gives them back. `canEdit`
 * false: read only, and nothing shown without links.
 */
const TaskLinksInlineField = ({ task, openerOf, options, canEdit, onSave }) => {
  const { text } = useText('tasks');
  const links = taskLinks(task);
  const field = useInlineField({
    value: links,
    same: sameLinks,
    onSave: (next) => onSave({ links: next.map(linkPayload) }),
  });
  const [more, setMore] = useState(false);
  const box = useRef(null);
  const commitRef = useRef(field.commit);
  commitRef.current = field.commit;
  const { editing } = field;
  // Opened, the search takes the focus and lists what can be added.
  useEffect(() => {
    if (editing) box.current?.querySelector('input')?.focus();
  }, [editing]);
  useEffect(() => {
    if (!editing) return undefined;
    const saveOutside = (event) => box.current && !box.current.contains(event.target) && commitRef.current();
    document.addEventListener('mousedown', saveOutside);
    return () => document.removeEventListener('mousedown', saveOutside);
  }, [editing]);

  if (editing) {
    return (
      <div
        ref={box}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          field.commit();
        }}
        // Focus gone to another control of the page (a tab out); a chip
        // removed takes the focus to nowhere, and keeps the field open.
        onBlur={(event) => event.relatedTarget && !box.current.contains(event.relatedTarget) && field.commit()}
      >
        <TaskLinksField
          label={text('columnLinked')}
          openUpwards={false}
          placeholder={LINKS_PLACEHOLDER}
          options={options}
          value={field.draft}
          onChange={field.setDraft}
        />
      </div>
    );
  }
  const shown = field.shown;
  if (!shown.length && !canEdit) return null;
  return (
    <div className="flex flex-col gap-2">
      {canEdit ? (
        <button
          type="button"
          onClick={field.start}
          className={`group flex items-center gap-1.5 self-start rounded text-left hover:text-gray-900 ${LABEL_CLASS}`}
        >
          {text('columnLinked')}
          <span aria-hidden="true" className="opacity-0 transition-opacity group-hover:opacity-100">
            <Pencil wh={12} color="currentColor" />
          </span>
        </button>
      ) : (
        <Label>{text('columnLinked')}</Label>
      )}
      {shown.length > 0 && (
        <div className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200">
          {shown.slice(0, more ? shown.length : SHOWN_LINKS).map((link) => (
            <TaskContactCard key={linkKey(link)} link={link} task={task} onOpen={openerOf(link)} />
          ))}
        </div>
      )}
      {shown.length > SHOWN_LINKS && (
        <SeeMoreButton open={more} onClick={() => setMore((current) => !current)}>
          {more ? text('seeLessLinks') : text('seeMoreLinks', { count: shown.length - SHOWN_LINKS })}
        </SeeMoreButton>
      )}
    </div>
  );
};

export default TaskLinksInlineField;
